import type { RGBAColor } from '@kitware/vtk.js/types';

import {
  STROKE_WIDTH_ANNOTATION_TOOL_DEFAULT,
  TOOL_COLORS,
} from '@/src/config';
import { NO_NAME } from '@/src/constants';
import type { Maybe } from '@/src/types';
import { arrayEquals, cleanUndefined, sameFields } from '@/src/utils';
import type { LabelmapSegment } from '@/src/segmentation/model';
import { cssColorToRGBA, rgbaToCssColor } from '@/src/segmentation/color';

/**
 * Identity and shared appearance for everything drawn as one thing: a paint
 * mask on any image, a rectangle, a polygon. Appearance fields are absent
 * until set and mean "app default" while they are, so a configured or imported
 * segment that states nothing follows the default and a ruler's segment carries
 * no meaningless opacity.
 */
export type Segment = {
  id: string;
  name: string;
  color: RGBAColor;
  // State the user sets on the thing itself, so it holds on every image.
  visible: boolean;
  locked: boolean;
  fillOpacity?: number;
  outlineOpacity?: number;
  strokeWidth?: number;
};

export type SegmentInit = Partial<Omit<Segment, 'id'>>;

const DEFAULT_SEGMENT_COLOR = cssColorToRGBA(TOOL_COLORS[0]);

const APPEARANCE_DEFAULTS = {
  name: '',
  color: DEFAULT_SEGMENT_COLOR,
  visible: true,
  locked: false,
  fillOpacity: 1,
  outlineOpacity: 1,
  strokeWidth: STROKE_WIDTH_ANNOTATION_TOOL_DEFAULT,
};

/**
 * The one resolver. Every renderer, editor and encoder reads a segment
 * through it; nothing reads the optional fields directly, so an absent field
 * means the app default in exactly one place.
 */
export const resolveSegmentAppearance = (segment: Maybe<Segment>) => {
  const stated: Partial<Segment> = segment ?? {};
  const resolved = {
    ...APPEARANCE_DEFAULTS,
    ...cleanUndefined({
      name: stated.name,
      color: stated.color,
      visible: stated.visible,
      locked: stated.locked,
      fillOpacity: stated.fillOpacity,
      outlineOpacity: stated.outlineOpacity,
      strokeWidth: stated.strokeWidth,
    }),
  };
  return {
    ...resolved,
    // What every list and label shows for a segment whose name is empty.
    displayName: resolved.name || NO_NAME,
    cssColor: rgbaToCssColor(resolved.color),
  };
};

/**
 * The descriptor a record projects onto the label value its mask holds, all of
 * it resolved from the segment. The labelmap renderer and the .seg.nrrd writer
 * consume it.
 */
export const toLabelmapSegment = (
  segment: Maybe<Segment>,
  labelValue: number
) => {
  const resolved = resolveSegmentAppearance(segment);
  return {
    value: labelValue,
    name: resolved.name,
    color: [...resolved.color] as RGBAColor,
    visible: resolved.visible,
    locked: resolved.locked,
    fillOpacity: resolved.fillOpacity,
    outlineOpacity: resolved.outlineOpacity,
  };
};

export const sameLabelmapSegment = (
  { color, ...fields }: LabelmapSegment,
  { color: otherColor, ...otherFields }: LabelmapSegment
) => arrayEquals(color, otherColor) && sameFields(fields, otherFields);
